import React, { useEffect, useState } from "react";
import { getUserCoinsHistory } from "../../api/user";
import { MIN_BIO_WORDS, countWords } from "../../utils/utils";

const getTextColor = (state) => {
  if (state.status === 'completed') return 'text-[#004c50]'; // teal
  if (state.status === 'pending') return 'text-gray-500';
  return 'text-gray-600';
};

const getIconColor = (state) => {
  if (state.status === 'completed') return 'text-[#004c50]'; // teal checkmark
  return 'text-gray-400';
};

// Status glyphs are locked one-per-state and every variant renders into the same
const StatusIcon = ({ status }) => {
  if (status === 'completed') {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <path d="M4 10.5 8 14.5 16 5.5" />
      </svg>
    );
  }
  if (status === 'pending') {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6.5V10l2.5 1.5" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="7" />
    </svg>
  );
};

// Mobile gets no fixed width and hugs the right edge, so every spare pixel goes
// to the row's title and helper instead of padding this column. Desktop keeps the
// fixed width, where there is room to spare and it keeps both rows' coin icons on
// the same vertical line.
const RewardColumn = ({ amount, earned }) => (
  <div className="flex h-5 shrink-0 items-center justify-end gap-1.5 sm:w-40 sm:justify-start">
    <img
      src="/qushi-coins-icon-com.png"
      alt="QUSHi Coins"
      className={`h-5 w-5 object-contain ${earned ? 'opacity-30' : ''}`}
    />
    <span
      className={`whitespace-nowrap leading-5 ${
        earned
          ? 'text-sm font-medium text-gray-500'
          : 'text-base font-semibold text-gray-900'
      }`}
    >
      {amount} Coins{earned ? ' earned' : ''}
    </span>
  </div>
);

// Both checklist rows share this so their icon, title and reward column can
// never drift out of alignment with each other.
const ChecklistRow = ({ title, state, amount, className = '' }) => (
  <div className={`flex items-start justify-between gap-2 sm:gap-4 ${className}`}>
    <div className="flex items-start gap-3 flex-1">
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center ${getIconColor(state)}`}
      >
        <StatusIcon status={state.status} />
      </span>
      <div>
        {/* "Bio and country" is wide enough to break across two lines in the
            narrow mobile column; the title always stays on one. */}
        <h4 className="whitespace-nowrap font-medium text-gray-900 text-sm">
          {title}
        </h4>
        {/* A few helper strings are too long for one mobile line. Those states
            carry a shortText; everything else reuses text for both widths. */}
        <p className={`text-xs ${getTextColor(state)}`}>
          <span className="sm:hidden">{state.shortText ?? state.text}</span>
          <span className="hidden sm:inline">{state.text}</span>
        </p>
      </div>
    </div>
    <RewardColumn amount={amount} earned={state.earned} />
  </div>
);

const TraderIdentityRewardBanner = ({
  userinfo,
  profileImage,
  imgurl,
  bio,
  country_of_origin,
  picture_upload_status,
  bio_upload_status,
  account_created_at,
  refreshKey = 0,
}) => {
  const PROGRAM_CUTOFF = "2026-07-28";
  const userCreatedAt = account_created_at || userinfo?.created_at || userinfo?.createdAt;

  const [rewardedTypes, setRewardedTypes] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getUserCoinsHistory();
        const history = response?.data?.success ? response.data.history : [];
        if (!cancelled) {
          setRewardedTypes(new Set((history ?? []).map((row) => row.type)));
        }
      } catch (error) {
        console.error("Error fetching coin history:", error);
        if (!cancelled) setRewardedTypes(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const datePart = String(dateStr).split(' ')[0];
      const date = new Date(datePart);
      return isNaN(date.getTime()) ? null : date;
    } catch (e) {
      return null;
    }
  };

  const parsedCreatedAt = parseDate(userCreatedAt);
  const parsedCutoff = parseDate(PROGRAM_CUTOFF);
  const isEligible = !!parsedCreatedAt && !!parsedCutoff && parsedCreatedAt >= parsedCutoff;

  const pictureRewarded = !!rewardedTypes?.has('profile_picture_reward');
  const identityRewarded = !!rewardedTypes?.has('trader_identity_reward');

  if (rewardedTypes === null) {
    return null;
  }

  if (!isEligible || (pictureRewarded && identityRewarded)) {
    return null;
  }

  const getPictureState = () => {
    if (pictureRewarded) {
      return {
        status: 'completed',
        icon: '✅',
        text: 'Completed',
        earned: true,
      };
    }
    if (picture_upload_status === 'under_review') {
      return {
        status: 'pending',
        icon: '⏳',
        text: 'Picture pending',
        earned: false,
      };
    }
    // Reached only when the reward was never earned, so this is the user's
    // first picture being turned down - tell them to try a different one.
    if (picture_upload_status === 'rejected') {
      return {
        status: 'incomplete',
        icon: '○',
        text: 'Try another picture',
        earned: false,
      };
    }
    const hasFreshBlob = profileImage?.blob;
    const hasStoredImage = imgurl && imgurl.length > 0;

    if (hasFreshBlob || hasStoredImage) {
      return {
        status: 'pending',
        icon: '⏳',
        text: 'Picture pending',
        earned: false,
      };
    }
    return {
      status: 'incomplete',
      icon: '○',
      text: 'Upload a profile picture',
      shortText: 'Upload a picture',
      earned: false,
    };
  };

  // Bio and country row state
  const getBioCountryState = () => {
    if (identityRewarded) {
      return {
        status: 'completed',
        icon: '✅',
        text: 'Completed',
        earned: true,
      };
    }

    const bioFilled = countWords(bio) >= MIN_BIO_WORDS;
    const countryFilled = !!country_of_origin; // country_of_origin is an ID (number)
    const bioPending = bio_upload_status === 'under_review';
    const bioRejected = bio_upload_status === 'rejected';

    if (bioRejected) {
      return {
        status: 'incomplete',
        icon: '○',
        text: 'Update your bio',
        earned: false,
      };
    }

    if (bioPending && !countryFilled) {
      return {
        status: 'pending',
        icon: '⏳',
        text: 'Select your country. Bio pending.',
        shortText: 'Add country. Bio pending.',
        earned: false,
      };
    }

    // Pending (country selected + bio awaiting moderation)
    if (countryFilled && bioPending) {
      return {
        status: 'pending',
        icon: '⏳',
        text: 'Bio pending',
        earned: false,
      };
    }

    // Incomplete - specific helper
    if (!bioFilled && !countryFilled) {
      return {
        status: 'incomplete',
        icon: '○',
        text: 'Add a bio and select your country',
        shortText: 'Add a bio and country',
        earned: false,
      };
    }
    if (!countryFilled && bioFilled) {
      return {
        status: 'incomplete',
        icon: '○',
        text: 'Select your country',
        earned: false,
      };
    }
    if (!bioFilled && countryFilled) {
      return {
        status: 'incomplete',
        icon: '○',
        text: 'Add a bio',
        earned: false,
      };
    }

    return {
      status: 'pending',
      icon: '⏳',
      text: 'Bio pending',
      earned: false,
    };
  };

  const pictureState = getPictureState();
  const bioCountryState = getBioCountryState();

  return (
    <div className="max-w-6xl mx-auto mb-6 bg-gray-50 border border-gray-200 border-l-4 border-l-[#d7c588] rounded-lg py-4 px-4">
      <div className="mb-4 pb-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-1">Build your trader identity</h3>
        <p className="text-sm text-gray-600">Earn up to 150 QUSHi Coins</p>
      </div>

      {/* Row 1: Profile Picture */}
      <ChecklistRow
        title="Profile picture"
        state={pictureState}
        amount="100"
        className="mb-4"
      />

      {/* Row 2: Bio and Country */}
      <ChecklistRow
        title="Bio and country"
        state={bioCountryState}
        amount="50"
      />
    </div>
  );
};

export default TraderIdentityRewardBanner;
